import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';
import Computation from '@js/modules/computation';

import Notification from '@js/components/layouts/notification';

export const checkRideErrors = (rideData, {skipMetrics = false}) => {
    const formFields = {};
    for (const [fieldName, fieldValue] of rideData.entries()) {
        formFields[fieldName] = fieldValue;
    }

    const rideFieldErrors = {};

    if (Utils.isEmpty(formFields['ride[distance]']) || parseFloat(formFields['ride[distance]']) < parseFloat(window.settings.ride_min_distance)) {
        rideFieldErrors.distance = I18n.t('js.ride.errors.distance', {
            min: Computation.formatDistance(window.settings.ride_min_distance, {
                smartUnit: true,
                kUnit: false
            }),
            max: Computation.formatDistance(window.settings.ride_max_distance, {
                smartUnit: true,
                kUnit: true
            })
        });
    }

    if (!skipMetrics) {
        if (Utils.isEmpty(formFields['ride[duration]']) && Utils.isEmpty(formFields['ride[speed]'])) {
            rideFieldErrors.duration = I18n.t('js.ride.errors.duration', {
                min: Computation.formatDuration(window.settings.ride_min_duration, {withLeadingZero: false}),
                max: Computation.formatDuration(window.settings.ride_max_duration, {withSeconds: false})
            });
            rideFieldErrors.speed = I18n.t('js.ride.errors.speed', {
                min: Computation.formatSpeed(window.settings.ride_min_speed),
                max: Computation.formatSpeed(window.settings.ride_max_speed)
            });
        }
    }

    if (Utils.isEmpty(formFields['ride[name]']) || formFields['ride[name]'].length < parseInt(window.settings.ride_name_min_length)) {
        rideFieldErrors.name = I18n.t('js.ride.errors.name', {
            min: window.settings.ride_name_min_length,
            max: window.settings.ride_name_max_length
        });
    }

    if (Object.keys(formFields)
        .includes('ride[planned_at]') && Utils.isEmpty(formFields['ride[planned_at]'])) {
        rideFieldErrors.planned_at = I18n.t('js.ride.errors.planned_at');
    }

    return Utils.isPresent(rideFieldErrors) ? rideFieldErrors : false;
};

export const buildRideFormData = (form, parameters) => {
    const {
        name,
        rideType,
        visibility,
        distance,
        duration,
        durationComputed,
        breakTime,
        speed,
        speedComputed,
        isMapEdited,
        points,
        staticMap,
        planned,
        plannedAt,
        plannedPoiId,
        ridePois,
        shared,
        collaborative,
        collaborativeGroup,
        collaborativeGroupId,
        color,
        sections,
        surfaces,
        proposedPlannedAt
    } = parameters || {};

    const rideData = new FormData(form);

    if (shared) {
        rideData.append('shared', true);
        rideData.append('ride[shared]', true);
    }

    if (collaborative) {
        rideData.append('ride[collaborative]', true);
        if (collaborativeGroupId) {
            rideData.append('ride[collaborative_group_id]', collaborativeGroupId);
            if (color) {
                rideData.append('ride[color]', color);
            }
            if (proposedPlannedAt) {
                rideData.append('ride[collaborative_group][planned_at]', proposedPlannedAt);
            }
        } else if (collaborativeGroup) {
            Object.entries(collaborativeGroup).forEach(([key, value]) => {
                if (value != null && value !== '') {
                    rideData.append(`ride[collaborative_group][${key}]`, value);
                }
            });
        }
    }

    if (visibility) {
        rideData.append('ride[visibility]', visibility);
    }

    rideData.append('ride[distance]', distance);

    rideData.append('ride[duration_computed]', durationComputed);
    if (speedComputed) {
        rideData.append('ride[duration]', duration);
    }

    if (breakTime) {
        rideData.append('ride[break_time]', breakTime);
    }

    rideData.append('ride[speed_computed]', speedComputed);
    if (durationComputed) {
        rideData.append('ride[speed]', speed);
    }

    if (window.unit === 'mi') {
        rideData.append('ride[speed]', Computation.speedToUnit(speed, {unitTarget: 'kmh'}));
    }

    if (planned) {
        rideData.append('ride[planned]', true);

        if (name) {
            rideData.append('ride[name]', name);
        }
        if (rideType) {
            rideData.append('ride[ride_type]', rideType);
        }
        if (plannedAt) {
            rideData.append('ride[planned_at]', plannedAt);
        }
    }
    if (plannedPoiId) {
        rideData.append('ride[planned_poi_id]', plannedPoiId);
    }

    if (isMapEdited && points?.length) {
        rideData.append('ride[map_edited]', true);
        rideData.append('ride[map_points]', points);
        if (staticMap) {
            rideData.append('ride[static_map]', staticMap);
        }
    }

    if (ridePois?.length) {
        ridePois.map((ridePoi) => Object.keys(ridePoi)
            .forEach((poiDataName) => rideData.append(`ride[pois][][${poiDataName}]`, ridePoi[poiDataName])));
    }

    if (sections?.length) {
        sections.forEach((section) => {
            rideData.append('ride[sections][][from]', section.from);
            rideData.append('ride[sections][][to]', section.to);
            rideData.append('ride[sections][][color]', section.color);
        });
    } else if (Array.isArray(sections) && isMapEdited && points?.length) {
        rideData.append('ride[sections][][from]', '');
    }

    if (surfaces?.length) {
        surfaces.forEach((surface) => {
            rideData.append('ride[surfaces][][from]', surface.from);
            rideData.append('ride[surfaces][][to]', surface.to);
            rideData.append('ride[surfaces][][surface]', surface.surface);
        });
    } else if (Array.isArray(surfaces) && isMapEdited && points?.length) {
        rideData.append('ride[surfaces][][from]', '');
    }

    const rideErrors = checkRideErrors(rideData, {skipMetrics: shared || collaborative});

    if (rideErrors) {
        Notification.error(I18n.t('js.ride.errors.message'));

        return {
            errors: true,
            rideErrors
        };
    }

    return rideData;
};

// Polling of the ride processing job. Bounded on purpose: a job that never answers (worker down,
// queue backed up) has to end on a message the user can act on, not on an endless spinner.
const PROCESSING_POLL_INTERVAL = 400;
const PROCESSING_TIMEOUT = 120000;

// `onComplete` fires only once the server confirms the ride is processed: that is where the caller may
// drop its local draft. `onError` fires on a terminal failure, so the caller can re-enable its form —
// the draft is still there, the user only has to submit again.
export const processRide = (submissionResponse, checkJobProgress, {onComplete, onError} = {}) => {
    const rideId = submissionResponse?.ride?.id;

    if (!rideId) {
        return false;
    }

    let timerInterval;
    let elapsed = 0;
    let settled = false;

    import(/* webpackChunkName: "theme-swal" */ '@js/modules/swal')
        .then((sweetalert2) => {
            return sweetalert2.default;
        })
        .then((Swal) => {
            const _showMessage = (message) => {
                const messageContainer = Swal.getHtmlContainer();

                if (messageContainer && message) {
                    messageContainer.textContent = message;
                }
            };

            // Terminal failure: stop polling and give the user back control (hideLoading brings the
            // confirm button back) instead of spinning forever.
            const _fail = (message) => {
                if (settled) {
                    return;
                }

                settled = true;
                clearInterval(timerInterval);

                _showMessage(message || I18n.t('js.ride.common.processing_error'));
                Swal.hideLoading();

                if (onError) {
                    onError();
                }
            };

            return Swal.fire({
                title: I18n.t('js.ride.common.loading_process'),
                html: '<div id="ride-message"></div>',
                showCancelButton: false,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();

                    timerInterval = setInterval(() => {
                        elapsed += PROCESSING_POLL_INTERVAL;

                        if (elapsed >= PROCESSING_TIMEOUT) {
                            _fail(I18n.t('js.ride.common.processing_timeout'));

                            return;
                        }

                        checkJobProgress(rideId, (response) => {
                            if (settled) {
                                return;
                            }

                            if (!response) {
                                Swal.hideLoading();

                                return;
                            }

                            const {
                                processingMessage,
                                // currentStep,
                                totalSteps,
                                rideLink
                            } = response;

                            if (rideLink) {
                                settled = true;
                                clearInterval(timerInterval);

                                _showMessage(processingMessage);

                                if (onComplete) {
                                    onComplete();
                                }

                                // setTimeout(() => Swal.close(), 1000);
                                setTimeout(() => window.location = rideLink, 300);

                                return;
                            }

                            if (totalSteps === 0) {
                                // Problem when processing ride data
                                _fail(processingMessage);

                                return;
                            }

                            _showMessage(processingMessage);
                        });
                    }, PROCESSING_POLL_INTERVAL);
                },
                willClose: () => {
                    clearInterval(timerInterval);

                    // Dismissed (escape key) while still processing: nothing was confirmed, so the
                    // caller must be able to submit again.
                    if (!settled) {
                        settled = true;

                        if (onError) {
                            onError();
                        }
                    }
                }
            });
        });
};

export const checkPoiErrors = (poiData, plan = false) => {
    const poiFieldErrors = {};

    if (Utils.isEmpty(poiData.get('poi[title]'))) {
        poiFieldErrors.title = I18n.t('js.poi.errors.title');
    }

    return Utils.isPresent(poiFieldErrors) ? poiFieldErrors : false;
};

export const buildPoiFormData = (form, parameters) => {
    const {
        plan,
        poiCoordinates
    } = parameters || {};

    const poiData = new FormData(form);

    if (plan) {
        poiData.append('plan', true);
        poiData.append('poi[status]', 'place');
    }

    if (poiCoordinates) {
        poiData.append('poi[latitude]', poiCoordinates.lat);
        poiData.append('poi[longitude]', poiCoordinates.lng);
    }

    const poiErrors = checkPoiErrors(poiData, plan);

    if (poiErrors) {
        return {
            errors: true,
            poiErrors
        };
    }

    return poiData;
};

// Ends closer than this are the same spot: the route is a loop, and "Autour de X" reads better
// than "De X à X". Same threshold as the mobile app, which suggests the title the same way.
export const ROUTE_LOOP_THRESHOLD_METERS = 60;

// A concise place label for a route title, from a reverse-geocoded location. For a plain
// street/house ("address") the city reads better than "Rue des Écoles", so prefer the first (city)
// token of the place name; for a named feature (peak, place, poi) the feature's own name is the
// meaningful label. Null when nothing usable came back.
export const routeTitleLabel = (location) => {
    if (!location) {
        return null;
    }

    const city = (location.placeName || '').split(',')[0]
        .trim();
    const label = (location.placeType === 'address' ? city : (location.name || '').trim() || city).trim();

    return label.length ? label : null;
};

// "De X à Y" for a point-to-point route, "Autour de X" for a loop — or when both ends resolve to
// the same place, which a long out-and-back does. Empty string when neither end could be named:
// the field then simply stays empty, as it did before.
export const buildRouteTitle = (startLocation, endLocation, {loop = false} = {}) => {
    const start = routeTitleLabel(startLocation);
    const end = routeTitleLabel(endLocation);

    if (loop || (start && end && start === end)) {
        return start ? I18n.t('js.ride.common.form.suggested_name.around', {place: start}) : '';
    }

    if (start && end) {
        return I18n.t('js.ride.common.form.suggested_name.from_to', {
            start,
            end
        });
    }

    return start || end || '';
};
